import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Banknote,
  RefreshCw,
  DollarSign,
  TrendingDown,
  Wallet,
  Printer,
  FileDown,
  Search,
  Calendar,
  Download,
  X,
} from 'lucide-react';
import DataTable from '../../components/shared/DataTable';
import DatePicker from '../../components/client/DatePicker';
import { getVendorPayoutSummary, getVendorPayoutDisbursements } from '../../services/vendorPayoutService';
import { openPrintWindow, saveHtmlAsPdf, escapeHtml } from '../../utils/reportExport';
import { useToastContext } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';

const fmtMoney = (n) => {
  const v = Number(n);
  if (Number.isNaN(v)) return '—';
  return `$${v.toFixed(2)}`;
};

const EMPTY_DISBURSEMENT_ROWS = [];

const DATE_PRESETS = [
  { id: 'all', label: 'All time' },
  { id: '30d', label: 'Last 30 days' },
  { id: '90d', label: 'Last 90 days' },
  { id: 'ytd', label: 'Year to date' },
  { id: 'custom', label: 'Custom range' },
];

const toIsoDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getPresetRange = (presetId) => {
  const today = new Date();
  const end = toIsoDate(today);

  if (presetId === '30d') {
    const start = new Date(today);
    start.setDate(start.getDate() - 29);
    return { date_from: toIsoDate(start), date_to: end };
  }
  if (presetId === '90d') {
    const start = new Date(today);
    start.setDate(start.getDate() - 89);
    return { date_from: toIsoDate(start), date_to: end };
  }
  if (presetId === 'ytd') {
    return { date_from: `${today.getFullYear()}-01-01`, date_to: end };
  }
  return { date_from: '', date_to: '' };
};

const buildFilterParams = ({ page, perPage, dateFrom, dateTo, search, exportAll = false }) => {
  const params = { page, per_page: perPage };
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;
  if (search?.trim()) params.search = search.trim();
  if (exportAll) params.export = 1;
  return params;
};

const formatFilterLabel = (dateFrom, dateTo, search) => {
  const parts = [];
  if (dateFrom && dateTo) parts.push(`${dateFrom} → ${dateTo}`);
  else if (dateFrom) parts.push(`From ${dateFrom}`);
  else if (dateTo) parts.push(`Through ${dateTo}`);
  if (search?.trim()) parts.push(`Search: "${search.trim()}"`);
  return parts.length ? parts.join(' · ') : 'All disbursements';
};

const VendorPayoutDisbursements = () => {
  const { user } = useAuth();
  const { success, error: showError } = useToastContext();
  const [page, setPage] = useState(1);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [datePreset, setDatePreset] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const perPage = 15;

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const hasActiveFilters = Boolean(dateFrom || dateTo || search.trim());

  const summaryQuery = useQuery({
    queryKey: ['vendor-payout-summary'],
    queryFn: async () => getVendorPayoutSummary(),
  });

  const listQuery = useQuery({
    queryKey: ['vendor-payout-disbursements', page, dateFrom, dateTo, search],
    queryFn: async () =>
      getVendorPayoutDisbursements(buildFilterParams({ page, perPage, dateFrom, dateTo, search })),
    placeholderData: (prev) => prev,
  });

  const summary = summaryQuery.data?.data;
  const pagination = listQuery.data?.data;
  const listMeta = listQuery.data?.meta;
  const rows = useMemo(() => {
    const d = pagination?.data;
    return Array.isArray(d) ? d : EMPTY_DISBURSEMENT_ROWS;
  }, [pagination?.data]);

  const applyPreset = (presetId) => {
    setDatePreset(presetId);
    if (presetId === 'custom') return;
    const range = getPresetRange(presetId);
    setDateFrom(range.date_from);
    setDateTo(range.date_to);
    setPage(1);
  };

  const handleDateFromChange = (value) => {
    setDatePreset('custom');
    setDateFrom(value);
    setPage(1);
  };

  const handleDateToChange = (value) => {
    setDatePreset('custom');
    setDateTo(value);
    setPage(1);
  };

  const clearFilters = () => {
    setDatePreset('all');
    setDateFrom('');
    setDateTo('');
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const fetchExportRows = useCallback(async () => {
    const response = await getVendorPayoutDisbursements(
      buildFilterParams({ page: 1, perPage, dateFrom, dateTo, search, exportAll: true })
    );
    const exportRows = Array.isArray(response?.data) ? response.data : response?.data?.data;
    if (!Array.isArray(exportRows)) {
      throw new Error('Could not load disbursements for export.');
    }
    return {
      rows: exportRows,
      meta: response?.meta || {},
    };
  }, [dateFrom, dateTo, search]);

  const buildPrintBodyHtml = useCallback(
    (exportRows, exportMeta, filterLabel) => {
      const vendorLine = user?.name || user?.email ? escapeHtml(user.name || user.email) : '';
      const filteredTotal = exportMeta?.filtered_total_amount ?? exportRows.reduce((sum, r) => sum + Number(r.amount || 0), 0);
      const filteredCount = exportMeta?.filtered_count ?? exportRows.length;
      const truncatedNote = exportMeta?.truncated
        ? 'Export limited to the first 5,000 matching records.'
        : '';

      const tableRows = exportRows
        .map(
          (r) => `<tr>
          <td>${escapeHtml(formatDt(r.created_at))}<br/><span style="font-size:10px;color:#666">${escapeHtml(r.disbursement_reference || '')}</span></td>
          <td><strong>${escapeHtml(fmtMoney(r.amount))}</strong></td>
          <td>${escapeHtml(r.booking?.booking_reference || '—')}</td>
          <td>${escapeHtml(r.notes || '—')}</td>
        </tr>`
        )
        .join('');

      return `
      <h1>Payouts from VendaList</h1>
      <p class="muted">${vendorLine ? `Vendor: ${vendorLine}<br/>` : ''}Generated ${escapeHtml(
        new Date().toLocaleString()
      )}</p>
      <p class="muted"><strong>Filter:</strong> ${escapeHtml(filterLabel)}</p>
      <div class="grid">
        <div><div class="muted">Earned from orders (lifetime)</div><div><strong>${escapeHtml(
          fmtMoney(summary?.total_earned_from_orders)
        )}</strong></div></div>
        <div><div class="muted">Total sent to you (lifetime)</div><div><strong>${escapeHtml(
          fmtMoney(summary?.total_disbursed)
        )}</strong></div></div>
        <div><div class="muted">Available balance</div><div><strong>${escapeHtml(
          fmtMoney(summary?.available_balance)
        )}</strong></div></div>
        <div><div class="muted">Filtered payouts (${filteredCount})</div><div><strong>${escapeHtml(
          fmtMoney(filteredTotal)
        )}</strong></div></div>
      </div>
      <h2 style="font-size:16px;margin:20px 0 8px;">Disbursement history</h2>
      <table>
        <thead><tr><th>Date / Reference</th><th>Amount sent</th><th>Booking</th><th>Notes</th></tr></thead>
        <tbody>${tableRows || '<tr><td colspan="4">No payouts match your filters.</td></tr>'}</tbody>
      </table>
      ${truncatedNote ? `<p class="muted" style="margin-top:16px">${escapeHtml(truncatedNote)}</p>` : ''}
    `;
    },
    [user, summary]
  );

  const handlePrint = async () => {
    try {
      setExportBusy(true);
      const { rows: exportRows, meta: exportMeta } = await fetchExportRows();
      const filterLabel = formatFilterLabel(dateFrom, dateTo, search);
      const r = openPrintWindow(
        'Payouts from VendaList',
        buildPrintBodyHtml(exportRows, exportMeta, filterLabel)
      );
      if (!r.ok) showError('Allow pop-ups to print this report.');
    } catch (e) {
      showError(e?.message || 'Could not prepare print report.');
    } finally {
      setExportBusy(false);
    }
  };

  const handlePdf = async () => {
    setPdfBusy(true);
    try {
      const { rows: exportRows, meta: exportMeta } = await fetchExportRows();
      const filterLabel = formatFilterLabel(dateFrom, dateTo, search);
      const suffix = dateFrom || dateTo ? `${dateFrom || 'start'}-to-${dateTo || 'end'}` : 'all';
      await saveHtmlAsPdf(
        buildPrintBodyHtml(exportRows, exportMeta, filterLabel),
        `vendor-payouts-${suffix}`
      );
      success('PDF downloaded.');
    } catch (e) {
      showError(e?.message || 'Could not generate PDF.');
    } finally {
      setPdfBusy(false);
    }
  };

  const handleCsvExport = async () => {
    setExportBusy(true);
    try {
      const { rows: exportRows, meta: exportMeta } = await fetchExportRows();
      const escapeCsv = (val) => {
        const str = String(val ?? '');
        if (/[",\n\r]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
        return str;
      };

      const header = ['Date', 'Reference', 'Amount', 'Booking', 'Notes'].map(escapeCsv).join(',');
      const body = exportRows
        .map((r) =>
          [
            formatDt(r.created_at),
            r.disbursement_reference || '',
            Number(r.amount || 0).toFixed(2),
            r.booking?.booking_reference || '',
            r.notes || '',
          ]
            .map(escapeCsv)
            .join(',')
        )
        .join('\n');

      const filterLine = `# Filter: ${formatFilterLabel(dateFrom, dateTo, search)}`;
      const totalLine = `# Total (${exportMeta?.filtered_count ?? exportRows.length} payouts): ${fmtMoney(
        exportMeta?.filtered_total_amount ??
          exportRows.reduce((sum, r) => sum + Number(r.amount || 0), 0)
      )}`;
      const csv = `\uFEFF${filterLine}\n${totalLine}\n${header}\n${body}`;

      const suffix = dateFrom || dateTo ? `${dateFrom || 'start'}-to-${dateTo || 'end'}` : 'all';
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `vendor-payouts-${suffix}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      success('CSV exported.');
    } catch (e) {
      showError(e?.message || 'Could not export CSV.');
    } finally {
      setExportBusy(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        key: 'when',
        label: 'Date',
        render: (row) => (
          <div className="text-sm">
            <div className="font-medium text-gray-900">{formatDt(row.created_at)}</div>
            <div className="text-xs font-mono text-gray-500 truncate max-w-[200px]" title={row.disbursement_reference}>
              {row.disbursement_reference}
            </div>
          </div>
        ),
      },
      {
        key: 'amount',
        label: 'Amount sent',
        render: (row) => (
          <span className="text-sm font-semibold text-emerald-800">{fmtMoney(row.amount)}</span>
        ),
      },
      {
        key: 'booking',
        label: 'Booking',
        render: (row) => (
          <span className="text-sm text-gray-700">{row.booking?.booking_reference || '—'}</span>
        ),
      },
      {
        key: 'notes',
        label: 'Notes',
        render: (row) => (
          <span className="text-sm text-gray-600 line-clamp-2 max-w-xs">{row.notes || '—'}</span>
        ),
      },
    ],
    []
  );

  const filterLabel = formatFilterLabel(dateFrom, dateTo, search);

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="space-y-3 sm:space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
              <Banknote className="w-8 h-8 text-primary-600" />
              Payouts from VendaList
            </h1>
            <p className="text-sm text-gray-600 max-w-2xl">
              Amounts the platform has recorded as sent to you for order earnings. Filter by date, search
              references, and export matching payouts as CSV, PDF, or print.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-4 sm:mt-0">
            <button
              type="button"
              onClick={handleCsvExport}
              disabled={exportBusy || listQuery.isLoading}
              className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <Download className="w-4 h-4 mr-2" />
              {exportBusy ? 'Exporting…' : 'Export CSV'}
            </button>
            <button
              type="button"
              onClick={handlePrint}
              disabled={exportBusy || listQuery.isLoading}
              className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print
            </button>
            <button
              type="button"
              onClick={handlePdf}
              disabled={pdfBusy || exportBusy || listQuery.isLoading}
              className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-black text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-50"
            >
              <FileDown className="w-4 h-4 mr-2" />
              {pdfBusy ? 'PDF…' : 'Download PDF'}
            </button>
            <button
              type="button"
              onClick={() => {
                summaryQuery.refetch();
                listQuery.refetch();
              }}
              disabled={summaryQuery.isFetching || listQuery.isFetching}
              className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw
                className={`w-4 h-4 mr-2 ${summaryQuery.isFetching || listQuery.isFetching ? 'animate-spin' : ''}`}
              />
              Refresh
            </button>
          </div>
        </div>

        {summaryQuery.error && (
          <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm px-4 py-3">
            {summaryQuery.error.message || 'Could not load balance summary.'}
          </div>
        )}

        <div className="space-y-6 bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
          <div className="border-b border-gray-100 pb-4">
            <p className="text-xl font-bold text-gray-900">Payouts from VendaList</p>
            {(user?.name || user?.email) && (
              <p className="text-sm text-gray-600 mt-1">{user.name || user.email}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">{new Date().toLocaleString()}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-gray-50 sm:bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary-50 text-primary-700">
                <DollarSign className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Earned from orders</p>
                <p className="text-lg font-bold text-gray-900">{fmtMoney(summary?.total_earned_from_orders)}</p>
              </div>
            </div>
            <div className="bg-gray-50 sm:bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-50 text-amber-800">
                <TrendingDown className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total sent to you</p>
                <p className="text-lg font-bold text-gray-900">{fmtMoney(summary?.total_disbursed)}</p>
              </div>
            </div>
            <div className="bg-gray-50 sm:bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-50 text-emerald-800">
                <Wallet className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Available balance</p>
                <p className="text-lg font-bold text-emerald-900">{fmtMoney(summary?.available_balance)}</p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-gray-50/80 p-4 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <h2 className="text-sm font-semibold text-gray-900">Filter disbursements</h2>
              </div>
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="inline-flex items-center text-xs font-medium text-gray-600 hover:text-gray-900"
                >
                  <X className="w-3.5 h-3.5 mr-1" />
                  Clear filters
                </button>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              {DATE_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => applyPreset(preset.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    datePreset === preset.id
                      ? 'bg-gray-900 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">From date</label>
                <DatePicker
                  value={dateFrom}
                  onChange={handleDateFromChange}
                  placeholder="Start date"
                  maxDate={dateTo || undefined}
                  className="w-full text-sm"
                  size="sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">To date</label>
                <DatePicker
                  value={dateTo}
                  onChange={handleDateToChange}
                  placeholder="End date"
                  minDate={dateFrom || undefined}
                  className="w-full text-sm"
                  size="sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Reference, booking, or notes…"
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-1 border-t border-gray-200/80">
              <p className="text-xs text-gray-600">
                <span className="font-medium text-gray-800">{filterLabel}</span>
              </p>
              <span className="text-xs text-gray-400">·</span>
              <p className="text-xs text-gray-600">
                <span className="font-semibold text-emerald-800">{fmtMoney(listMeta?.filtered_total_amount)}</span>
                {' '}across{' '}
                <span className="font-semibold text-gray-900">{listMeta?.filtered_count ?? 0}</span>
                {' '}payout{listMeta?.filtered_count === 1 ? '' : 's'}
              </p>
              {listQuery.isFetching && !listQuery.isLoading && (
                <span className="text-xs text-gray-400">Updating…</span>
              )}
            </div>
          </div>

          <div className="space-y-4 pt-2">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <h2 className="text-lg font-semibold text-gray-900">Disbursement history</h2>
              <p className="text-xs text-gray-500">
                CSV, print, and PDF include all rows matching your filters (not just this page).
              </p>
            </div>
            {listQuery.error && (
              <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm px-4 py-3">
                {listQuery.error.message || 'Could not load disbursements.'}
              </div>
            )}
            <DataTable
              columns={columns}
              data={rows}
              loading={listQuery.isLoading && !listQuery.data}
              searchable={false}
              filterable={false}
              sortable={false}
              showPagination={false}
              emptyMessage={
                hasActiveFilters ? 'No payouts match your filters.' : 'No payouts recorded yet.'
              }
            />
            {pagination && pagination.last_page > 1 && (
              <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-gray-100 print:hidden">
                <p className="text-sm text-gray-600">
                  Page {pagination.current_page} of {pagination.last_page}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={pagination.current_page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-40"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={pagination.current_page >= pagination.last_page}
                    onClick={() => setPage((p) => p + 1)}
                    className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

function formatDt(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default VendorPayoutDisbursements;
